import React, { useState } from 'react';
import { ui, btnPrimary, btnGhost, microLabel, agentVizView } from './canvasUi';
import ManualTableGrid from './ManualTableGrid';
import { ChartBlock, PieBlock } from '../shared/blocks/ResultBlocks';
import { gridFromRows, rowsFromGrid, gridFromChart, chartFromGrid } from './blockEdit';

/**
 * Edit a block's rendered output in place — the figures included.
 *
 * A run is the source of truth: re-running the block overwrites whatever is
 * typed here. The footer says so, because the alternative is someone carefully
 * rewording an agent's analysis and losing it to a Refresh all with no warning.
 *
 * Tables, an agent's viz table and a chart's series all reuse ManualTableGrid —
 * a chart is a two-column table (label, value) in disguise, so the chart editor
 * costs nothing extra and behaves identically to the others.
 */

const s = {
    wrap: { height: '100%', display: 'flex', flexDirection: 'column', gap: '12px', overflow: 'auto' },
    field: { display: 'flex', flexDirection: 'column', gap: '5px' },
    label: { ...microLabel },
    input: { width: '100%', boxSizing: 'border-box', padding: '7px 9px', borderRadius: ui.radiusSm, border: ui.border, fontSize: '13px', fontFamily: 'inherit', outline: 'none', color: ui.ink },
    number: { fontSize: '20px', fontWeight: 700, fontVariantNumeric: 'tabular-nums' },
    area: { width: '100%', boxSizing: 'border-box', padding: '8px 10px', borderRadius: ui.radiusSm, border: ui.border, fontSize: '13px', fontFamily: 'inherit', outline: 'none', color: ui.ink, resize: 'vertical', minHeight: '84px', lineHeight: 1.6 },
    foot: { display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginTop: 'auto', paddingTop: '10px', borderTop: `1px solid ${ui.hairline}` },
    btn: { ...btnPrimary, padding: '6px 14px', fontSize: '12.5px' },
    ghost: { ...btnGhost, padding: '6px 12px', fontSize: '12.5px' },
    note: { fontSize: '11px', color: ui.ink2, flexBasis: '100%', lineHeight: 1.5 },

    // Live chart preview. The chart components carry their own 24px bottom
    // margin for the chat layout, which is dead space in a cramped editor.
    preview: { flexShrink: 0, marginBottom: '-16px' },
};

/** The chart presentation fields a draft carries, seeded from Chart_data. */
function chartLabelsFrom(cd) {
    return {
        header: cd?.chart_header || '',
        xLabel: cd?.x_axis_label || '',
        yLabel: cd?.y_axis_label || '',
        seriesName: cd?.series_name || '',
        showLegend: cd?.show_legend !== false,
    };
}

/**
 * A draft's chart fields → Chart_data.
 *
 * The single builder used by BOTH the live preview and the save path, so the
 * chart on screen while editing is byte-for-byte the chart that gets stored.
 * Two separate constructions here would be a preview that quietly lies.
 *
 * Empty label fields are OMITTED rather than written as "": the renderers test
 * truthiness to decide whether to draw an axis label at all, and `show_legend`
 * is only written when the user actually turned it off, so a chart nobody
 * touched keeps its original shape.
 */
function chartDataFromDraft(d, previous) {
    const base = chartFromGrid(d.grid, { ...(previous || {}), chart_type: d.chartType });
    const out = { ...base };
    if (d.header && d.header.trim()) out.chart_header = d.header.trim();
    if (d.seriesName && d.seriesName.trim()) out.series_name = d.seriesName.trim();
    else delete out.series_name;
    if (d.xLabel && d.xLabel.trim()) out.x_axis_label = d.xLabel.trim();
    else delete out.x_axis_label;
    if (d.yLabel && d.yLabel.trim()) out.y_axis_label = d.yLabel.trim();
    else delete out.y_axis_label;
    if (d.showLegend === false) out.show_legend = false;
    else delete out.show_legend;
    return out;
}

/** The editable draft for a block type, seeded from its current result. */
function draftFrom(block) {
    const r = block.result || {};
    if (block.type === 'scorecard') return { value: String(r.value ?? ''), label: r.label || '' };
    if (block.type === 'table') return { grid: gridFromRows(r.rows) };
    if (block.type === 'chart') {
        return {
            grid: gridFromChart(r.Chart_data),
            chartType: r.Chart_data?.chart_type || 'bar',
            ...chartLabelsFrom(r.Chart_data),
        };
    }
    // agent — edit whichever half the CARD is currently showing, resolved by
    // the same shared helper the card uses. Keying off `viz.type` alone (as
    // this did) picked the chart for every both-halves result, so a reader
    // looking at the table would open the pencil onto chart data.
    const { view, hasTable } = agentVizView(block, r.viz);
    // A chart the reader derived from the table has no values of its own to
    // type over — its figures ARE the table's — so the editor opens on the
    // table, which is what the pencil can actually change.
    const derived = Boolean(block.config?.chartSettings?.[0]?.data);
    const editingTable = view === 'table' || (derived && hasTable);
    return {
        headline: r.headline || '',
        callouts: r.callouts || '',
        vizKind: editingTable ? 'table' : (view === null ? 'none' : view),
        chartType: r.viz?.Chart_data?.chart_type || 'bar',
        ...chartLabelsFrom(r.viz?.Chart_data),
        grid: editingTable
            ? gridFromRows(r.viz.table)
            : (r.viz?.Chart_data ? gridFromChart(r.viz.Chart_data) : null),
    };
}

/** The draft turned back into the result shape the server validates. */
export function resultFrom(block, d) {
    if (block.type === 'scorecard') return { value: d.value, label: d.label };
    if (block.type === 'table') return { rows: rowsFromGrid(d.grid) };
    if (block.type === 'chart') {
        return { Chart_data: chartDataFromDraft(d, block.result?.Chart_data) };
    }
    const out = { headline: d.headline, callouts: d.callouts };
    const prev = block.result?.viz;
    // The UNEDITED half is carried through verbatim. A result can hold both a
    // chart and a table, the editor only ever shows one of them, and
    // sanitizeEditedResult stores exactly what it is sent — so without this,
    // saving an edit to the chart would delete the table behind the card's
    // Chart/Table switch (and the table the spreadsheet exports prefer), with
    // nothing on screen suggesting anything was lost.
    if (d.vizKind === 'table' && d.grid) {
        out.viz = { type: 'table', table: rowsFromGrid(d.grid) };
        if (prev?.Chart_data) {
            out.viz.Chart_data = prev.Chart_data;
            // Keep the chart flavour as the stored type; `table` alongside it
            // is what tells the card both halves exist. A literal "table" type
            // here would lose which chart kind to draw on switching back.
            out.viz.type = prev.type && prev.type !== 'table' ? prev.type : prev.Chart_data.chart_type;
        }
    } else if (d.vizKind === 'chart' && d.grid) {
        const Chart_data = chartDataFromDraft(d, prev?.Chart_data);
        out.viz = { type: prev?.type && prev.type !== 'table' ? prev.type : Chart_data.chart_type, Chart_data };
        if (Array.isArray(prev?.table) && prev.table.length > 0) out.viz.table = prev.table;
    }
    // Charts behind the first ride through untouched: the editor only ever
    // shows chart 1, and the server stores exactly what it is sent.
    const prevCharts = Array.isArray(prev?.charts) && prev.charts.length > 1 ? prev.charts : null;
    if (prevCharts && out.viz) {
        out.viz.charts = [out.viz.Chart_data || prevCharts[0], ...prevCharts.slice(1)];
    }
    return out;
}

/**
 * The chart editing surface: the chart itself, live, above the values grid.
 *
 * Only the VALUES live here now. Title, type, axis labels and the legend moved
 * to Chart settings (config, survives a re-run) — two homes for a title was
 * exactly the confusion the old grid/title pair caused. The preview is still
 * built with the same chartDataFromDraft the save path uses, so what is on
 * screen while editing is exactly what gets stored.
 */
function ChartFields({ d, set, previous, onChartSettings }) {
    const preview = chartDataFromDraft(d, previous);
    const isPie = d.chartType === 'pie';

    return (
        <>
            <div style={s.preview} data-testid="canvas-chart-preview">
                {isPie ? <PieBlock chartData={preview} /> : <ChartBlock chartData={preview} />}
            </div>
            {onChartSettings && (
                <button type="button" className="cvx-ghost cvx-focus" style={{ ...s.ghost, alignSelf: 'flex-start' }} onClick={onChartSettings} data-testid="canvas-edit-chart-settings">
                    Chart settings → title, type, colors, labels
                </button>
            )}
            <ManualTableGrid cfg={d.grid} onChange={(grid) => set({ grid })} compact />
        </>
    );
}

export default function BlockResultEditor({ block, saving, onSave, onCancel, onChartSettings }) {
    const [d, setD] = useState(() => draftFrom(block));
    const set = (patch) => setD((cur) => ({ ...cur, ...patch }));

    return (
        <div style={s.wrap}>
            {block.type === 'scorecard' && (
                <>
                    <div style={s.field}>
                        <label style={s.label} htmlFor="cvx-sc-value">Value</label>
                        <input id="cvx-sc-value" className="cvx-focus" style={{ ...s.input, ...s.number }} value={d.value}
                            onChange={(e) => set({ value: e.target.value })} data-testid="canvas-edit-value" />
                    </div>
                    <div style={s.field}>
                        <label style={s.label} htmlFor="cvx-sc-label">Label</label>
                        <input id="cvx-sc-label" className="cvx-focus" style={s.input} value={d.label} maxLength={200}
                            onChange={(e) => set({ label: e.target.value })} />
                    </div>
                </>
            )}

            {block.type === 'table' && (
                <ManualTableGrid cfg={d.grid} onChange={(grid) => set({ grid })} compact />
            )}

            {block.type === 'chart' && (
                <ChartFields d={d} set={set} previous={block.result?.Chart_data} onChartSettings={onChartSettings} />
            )}

            {block.type === 'agent' && (
                <>
                    <div style={s.field}>
                        <label style={s.label} htmlFor="cvx-ag-head">Headline</label>
                        <input id="cvx-ag-head" className="cvx-focus" style={s.input} value={d.headline} maxLength={300}
                            onChange={(e) => set({ headline: e.target.value })} data-testid="canvas-edit-headline" />
                    </div>
                    <div style={s.field}>
                        <label style={s.label} htmlFor="cvx-ag-calls">Callouts (one per line)</label>
                        <textarea id="cvx-ag-calls" className="cvx-focus" style={s.area} value={d.callouts}
                            onChange={(e) => set({ callouts: e.target.value })} data-testid="canvas-edit-callouts" />
                    </div>
                    {/* An agent's chart gets the SAME live preview and label
                        controls a chart block does — it is the same Chart_data
                        and the same renderer, so there is no reason for the
                        two editors to differ. Its table half stays a plain
                        grid; a table needs no preview, it IS one. */}
                    {d.vizKind === 'chart' && d.grid && (
                        <ChartFields d={d} set={set} previous={block.result?.viz?.Chart_data} onChartSettings={onChartSettings} />
                    )}
                    {d.vizKind === 'table' && d.grid && (
                        <div style={s.field}>
                            <label style={s.label}>Table</label>
                            <ManualTableGrid cfg={d.grid} onChange={(grid) => set({ grid })} compact />
                        </div>
                    )}
                </>
            )}

            <div style={s.foot}>
                <button type="button" className="cvx-primary cvx-focus" style={s.btn} disabled={saving}
                    onClick={() => onSave(resultFrom(block, d))} data-testid="canvas-edit-save">
                    {saving ? 'Saving…' : 'Save'}
                </button>
                <button type="button" className="cvx-ghost cvx-focus" style={s.ghost} onClick={onCancel} disabled={saving}>
                    Cancel
                </button>
                <span style={s.note}>Running this block again replaces these edits with fresh results.</span>
            </div>
        </div>
    );
}
